import { Fragment, useCallback, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery } from "react-query"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"

import BatchPredictionOutlinedIcon from "@mui/icons-material/BatchPredictionOutlined"
import BoltIcon from "@mui/icons-material/Bolt"
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined"
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined"
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined"
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined"
import IntegrationInstructionsOutlinedIcon from "@mui/icons-material/IntegrationInstructionsOutlined"
import MediationOutlinedIcon from "@mui/icons-material/MediationOutlined"
import ModelTrainingIcon from "@mui/icons-material/ModelTraining"
import OnlinePredictionIcon from "@mui/icons-material/OnlinePrediction"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined"
import { Grid } from "@mui/material"
import { Button, DateRangePicker, InputText, Menu, MenuItem, Skeleton, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { CustomArrowAdornment } from "../components/UI/CustomArrowAdornment"
import { ProjectConfigurationDialog } from "../components/dialogs/ProjectConfigurationDialog"
import { ModelType } from "../components/status/ModelType"
import { CopyUUIDButton } from "../components/utility/CopyUUIDButton"
import { SwitchDialog, setCurrentModel } from "../features/Models"
import { KonanAPI } from "../services/KonanAPI"
import type { RootState } from "../store/ReduxStore"
import type { Route } from "../types/custom/projects"
import type { ModelListCreate } from "../types/generated/api/ModelListCreate"
import type { PaginatedModelListCreateList } from "../types/generated/api/PaginatedModelListCreateList"
import { useIsPermitted } from "../utils/PermissionsHelpers"
import { extractPageFromBackEndPaginationLink, formatDistance } from "../utils/genericHelpers"
import { getModelByUUID } from "../utils/modelDetailsHelpers"
import { useDateQuery } from "./useDateQuery"

/**
 * Custom hook, which contains all routes data, used with useHeaderData.tsx to obtain
 * the current route data needed for the page header
 */
export function useRoutesHeaderData(): Record<string, Route> {
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  const DATE_ROUTES = ["/requests", "/overview", "decisions"]

  const { t } = useTranslation("routesHeaderData")

  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const [_, setSearchParams] = useSearchParams()

  const [startDate, setStartDate, endDate, setEndDate] = useDateQuery(29, DATE_ROUTES)

  const dispatch = useDispatch()

  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions?.flattenedKonanPermissions)

  const canSwitchModelState = useIsPermitted("Switch model state", permissions.konan, flattenedKonanPermissions)

  const inputRef = useRef<HTMLInputElement>(null)

  const [configurationDialogOpen, setConfigurationDialogOpen] = useState<boolean>(false)

  const [isSwitchDialogOpen, setIsSwitchDialogOpen] = useState<boolean>(false)
  const [modelsMenuAnchor, setModelsMenuAnchor] = useState<HTMLElement | null>(null)
  const isMenuOpen = Boolean(modelsMenuAnchor)

  const handleAdornmentClick = (): void => {
    if (inputRef?.current) {
      if (isMenuOpen) {
        setModelsMenuAnchor(null)
      } else {
        setModelsMenuAnchor(inputRef.current.parentElement)
        inputRef?.current.focus()
      }
    }
  }

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastModelRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isModelsLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  const {
    data: models,
    isLoading: isModelsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedModelListCreateList>, AxiosError, ModelListCreate>(
    ["models", currentProject?.uuid, "paginated", "state"],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchModels({
        projectId: currentProject?.uuid as string,
        page: pageParam,
        ordering: "state",
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<ModelListCreate[]>(
          (accumulator, page) => accumulator.concat(page.data.results as ModelListCreate[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      refetchOnMount: true,
      enabled: !!currentModel?.uuid && !!currentProject?.uuid && window.location.pathname.includes("/models"),
    },
  )

  const memoizedGetModelByUUID = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (currentModel: any) => getModelByUUID(currentModel, models?.pages),
    [models?.pages],
  )

  const routes = {
    kcr: {
      title: t("modelsRegistry.title"),
      description: t("modelsRegistry.description"),
      icon: <DnsOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
    },

    performance: {
      title: t("insights.title"),
      icon: <DashboardOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
      actionButtons: [
        <CopyUUIDButton
          key={currentProject?.uuid}
          disabled={!currentProject?.uuid}
          textToCopy={currentProject?.uuid as string}
        />,
      ],
    },

    models: {
      title: t("models.title"),
      description: t("models.description"),
      icon: <BatchPredictionOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
    },

    workflows: {
      title: t("workflows.title"),
      description: t("workflows.description"),
      icon: <MediationOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
    },

    settings: {
      title: t("settings.title"),
      description: t("settings.description"),
      icon: <SettingsOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
    },

    decisions: {
      title: t("decisions.title"),
      description: currentProject?.uuid ? `${t("decisions.description")} ${currentProject?.category}.` : "",
      icon: <BoltIcon style={{ color: "var(--blue-text-2)" }} />,
      actionButtons: [
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          disableFuture
          key={currentProject?.uuid}
        />,
        <CopyUUIDButton
          key={currentProject?.uuid}
          disabled={!currentProject?.uuid}
          textToCopy={currentProject?.uuid as string}
        />,
      ],
    },

    dataFiles: {
      title: t("dataFiles.title"),
      icon: <FolderOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
      actionButtons: [
        <CopyUUIDButton
          key={currentProject?.uuid}
          disabled={!currentProject?.uuid}
          textToCopy={currentProject?.uuid as string}
        />,
      ],
    },

    workflow: {
      title: currentProject?.name ?? "",
      icon: <MediationOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
      description: currentProject?.description,
      actionButtons: [
        <Button
          key={currentProject?.name}
          variant="secondary"
          onClick={() => setConfigurationDialogOpen(true)}
          startIcon={<SettingsOutlinedIcon fontSize="small" />}
          size="regular"
        >
          {t("configurationBtn")}
        </Button>,
        <CopyUUIDButton
          key={currentProject?.uuid}
          disabled={!currentProject?.uuid}
          textToCopy={currentProject?.uuid as string}
        />,
      ],
      dialog: (
        <ProjectConfigurationDialog
          isOpen={configurationDialogOpen}
          onClose={() => setConfigurationDialogOpen(false)}
        />
      ),
    },

    assistant: {
      title: t("assistant.title"),
      icon: <TipsAndUpdatesOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
      actionButtons: [
        <CopyUUIDButton
          key={currentProject?.uuid}
          disabled={!currentProject?.uuid}
          textToCopy={currentProject?.uuid as string}
        />,
      ],
      dialog: (
        <ProjectConfigurationDialog
          isOpen={configurationDialogOpen}
          onClose={() => setConfigurationDialogOpen(false)}
        />
      ),
    },

    integration: {
      title: t("integration.title"),
      icon: <IntegrationInstructionsOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
      actionButtons: [
        <CopyUUIDButton
          key={currentProject?.uuid}
          disabled={!currentProject?.uuid}
          textToCopy={currentProject?.uuid as string}
        />,
      ],
      description: t("integration.description"),
    },

    overview: {
      title: t("overview.title"),
      description: t("overview.description"),
      icon: <DashboardOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
      actionButtons: [
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          disableFuture
          key={currentProject?.uuid}
        />,
        <CopyUUIDButton
          key={currentProject?.uuid}
          disabled={!currentProject?.uuid}
          textToCopy={currentProject?.uuid as string}
        />,
      ],
    },

    requests: {
      title: t("liveMonitoring.title"),
      description: t("liveMonitoring.description"),
      icon: <OnlinePredictionIcon style={{ color: "var(--blue-text-2)" }} />,
      actionButtons: [
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          disableFuture
          key={currentProject?.uuid}
        />,
        <CopyUUIDButton
          key={currentProject?.uuid}
          disabled={!currentProject?.uuid}
          textToCopy={currentProject?.uuid as string}
        />,
      ],
    },

    modelsList: {
      title: t("modelsList.title"),
      description: t("modelsList.description"),
      icon: <BatchPredictionOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
      actionButtons: [
        <Button
          key={currentProject?.name}
          variant="secondary"
          onClick={() => setConfigurationDialogOpen(true)}
          startIcon={<SettingsOutlinedIcon fontSize="small" />}
          size="regular"
        >
          {t("configurationBtn")}
        </Button>,
        <CopyUUIDButton
          key={currentProject?.uuid}
          disabled={!currentProject?.uuid}
          textToCopy={currentProject?.uuid as string}
        />,
      ],
      dialog: (
        <ProjectConfigurationDialog
          isOpen={configurationDialogOpen}
          onClose={() => setConfigurationDialogOpen(false)}
        />
      ),
    },

    modelTraining: {
      title: t("modelTraining.title"),
      description: t("modelTraining.description"),
      icon: <ModelTrainingIcon style={{ color: "var(--blue-text-2)" }} />,
      actionButtons: [
        <CopyUUIDButton
          key={currentProject?.uuid}
          disabled={!currentProject?.uuid}
          textToCopy={currentProject?.uuid as string}
        />,
      ],
    },

    classification: {
      title: !currentModel?.uuid ? (
        <Skeleton variant="rectangular" width="250px" height={"28px"} />
      ) : (
        <Fragment>
          <InputText
            isSelect
            fullWidth
            placeholder={t("classification.placeholder")}
            isNotchedLabel
            endAdornment={<CustomArrowAdornment isMenuOpen={isMenuOpen} handleAdornmentClick={handleAdornmentClick} />}
            handleInputClick={handleAdornmentClick}
            anchoringRef={inputRef}
            hideDescription
            value={currentModel?.name}
          />
          <Menu open={isMenuOpen} onClose={() => setModelsMenuAnchor(null)} anchorEl={modelsMenuAnchor} menuMaxContent>
            {models?.pages.map((model, index) => {
              return (
                <MenuItem
                  key={model.uuid}
                  isSelected={model.uuid === currentModel?.uuid}
                  onClick={() => {
                    dispatch(setCurrentModel(memoizedGetModelByUUID(model.uuid) ?? undefined))

                    setSearchParams((prev) => {
                      prev.set("model_id", model.uuid)
                      return prev
                    })

                    setModelsMenuAnchor(null)
                  }}
                  ref={hasNextPage && index === models.pages.length - 1 ? lastModelRef : undefined}
                >
                  <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Grid item>{model.name}</Grid>
                    <Grid item>
                      <ModelType modelState={model.state} size="small" />
                    </Grid>
                  </Grid>
                </MenuItem>
              )
            })}
            {hasNextPage &&
              [1, 2, 3].map((item: number) => (
                <Grid item xs={12} key={item} mt={1}>
                  <Skeleton height={25} width={"100%"} />
                </Grid>
              ))}
          </Menu>
        </Fragment>
      ),
      icon: <BatchPredictionOutlinedIcon style={{ color: "var(--blue-text-2)" }} />,
      dialog:
        isSwitchDialogOpen && canSwitchModelState ? (
          <SwitchDialog isOpen={isSwitchDialogOpen} setIsOpen={setIsSwitchDialogOpen} />
        ) : (
          <></>
        ),
      tag: (
        <ModelType
          modelState={currentModel?.uuid ? currentModel?.state : undefined}
          margin={"0px 0px 4px 0px"}
          size="small"
        />
      ),
      createdAt: currentModel?.created_at && (
        <Typography variant="label-regular">{formatDistance(currentModel?.created_at)}</Typography>
      ),
      actionButtons: [
        useIsPermitted("Switch model state", permissions.konan, flattenedKonanPermissions) ? (
          <Grid item key={currentProject?.uuid}>
            <Button
              tooltip={currentModel?.state === "live" || !currentModel?.uuid ? "Can't switch a live model" : undefined}
              onClick={() => setIsSwitchDialogOpen(true)}
              disabled={currentModel?.state === "live" || !currentModel?.uuid}
              variant="secondary"
              startIcon={<CachedOutlinedIcon fontSize="small" />}
              size="regular"
            >
              {t("classification.switchBtn")}
            </Button>
          </Grid>
        ) : (
          <></>
        ),
        <CopyUUIDButton
          isModel
          key={currentProject?.uuid}
          disabled={!currentModel?.uuid}
          textToCopy={currentModel?.uuid as string}
        />,
      ],
    },
  }

  return routes
}
